import React, { useEffect, useState, useCallback, useRef } from "react";
import { Form, notification, Modal, type DatePickerProps, type TableColumnsType, type TableProps } from "antd";
import axios from "axios";
import moment from "moment";
import { useLocation } from "react-router-dom";

interface DataType {
     key: React.Key;
     item_code: string;
     item_name: string;
     uom: string;
     qty: number;
     location_code_rack?: string;
}

interface ItemDataType {
     key: React.Key;
     item_code: string;
     item_name: string;
     uom: string;
     qty: number;
     location_code_rack?: string;
}

interface Warehouse {
     warehouse_code: string;
     warehouse_name: string;
}

interface UserData {
     id: string | number;
     nama: string;
     jabatan: string;
     dealer_code: string;
     otoritas: string;
     kode_grup1: string;
     warehouse_code: string;
     dealer_name?: string;
     warehouse_name?: string;
     menu_otoritas?: string;
     users?: string;
     last_activity_timestamp?: string;
}

const useItemWHTransferRequestLogic = (userData: UserData | null) => {
     const location = useLocation();
     const initializedRef = useRef(false);
     const [form] = Form.useForm();
     const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
     const [isAddLineModalVisible, setIsAddLineModalVisible] = useState(false);
     const [originWarehouse, setOriginWarehouse] = useState<string>("");
     const [dataSource, setDataSource] = useState<ItemDataType[]>([]);
     const [isEditModalVisible, setIsEditModalVisible] = useState(false);
     const [editingItem, setEditingItem] = useState<ItemDataType | null>(null);
     const [autosaveStatus, setAutosaveStatus] = useState<"idle" | "processing" | "error" | "stopped">("stopped");
     const [lastSavedData, setLastSavedData] = useState<any>(null);
     const [isFormDisabled, setIsFormDisabled] = useState<boolean>(false);
     const [formValuesState, setFormValuesState] = useState<any>(null);
     const [isReceiptMode, setIsReceiptMode] = useState<boolean>(false);
     const [isAcceptRejectDisabled, setIsAcceptRejectDisabled] = useState<boolean>(true);

     useEffect(() => {
          if (initializedRef.current) return;
          const { requestData, fromReceipt } = location.state || {};
          const mapStatusToString = (status: number | string) => {
               const statusValue = parseInt(String(status), 10);
               switch (statusValue) {
                    case 1: return 'Draft';
                    case 2: return 'Accept';
                    case 3: return 'Ready';
                    case 4: return 'Reject';
                    case 5: return 'Closed';
                    case 6: return 'Intransit';
                    default: return String(status);
               }
          };

          if (fromReceipt) {
               setIsReceiptMode(true);
          }

          if (requestData) {
               const { header, items } = requestData;
               form.setFieldsValue({
                    ...header,
                    created: header.created_by_name,
                    status: mapStatusToString(header.status),
                    transfer_request_date: header.transfer_request_date ? moment(header.transfer_request_date) : null,
                    approval_date: header.approval_date ? moment(header.approval_date).format("DD/MM/YYYY HH:mm:ss") : null,
               });

               const itemsWithKeys = items.map((item: any, index: number) => ({
                    ...item,
                    key: item.item_code || index,
               }));
               setDataSource(itemsWithKeys);
               setOriginWarehouse(header.warehouse_origin);

               const initialData = { form: form.getFieldsValue(true), table: itemsWithKeys };
               setLastSavedData(initialData);
               setFormValuesState(initialData.form);
               setAutosaveStatus("idle");

          } else {
               form.resetFields();
               setDataSource([]);
               setIsReceiptMode(false);
               setOriginWarehouse("");
               form.setFieldsValue({ status: "Draft" });
               form.setFieldsValue({ transfer_request_date: moment() });
               if (userData && userData.kode_grup1) {
                    form.setFieldsValue({ created: userData.nama });
               }
               setFormValuesState(form.getFieldsValue(true));
          }

          const fetchWarehouses = async () => {
               try {
                    const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/warehouses`);
                    setWarehouses(response.data);
               } catch (error) {
                    console.error("Error fetching warehouses:", error);
               }
          };

          fetchWarehouses();

          setFormValuesState(form.getFieldsValue(true));
          initializedRef.current = true
     }, [form, userData, location.state]);

     const handleFormValuesChange = useCallback((changedValues: any, allValues: any) => {
          setFormValuesState(allValues);
     }, []);

     useEffect(() => {
          const status = form.getFieldValue("status");
          if (status === "Draft" || status === 1) {
               setIsFormDisabled(false);
          } else {
               setIsFormDisabled(true);
          }
     }, [formValuesState, form]);

     useEffect(() => {
          const status = form.getFieldValue("status");
          if (status === "Ready" || status === 3) {
               setIsAcceptRejectDisabled(false);
          } else {
               setIsAcceptRejectDisabled(true);
          }
     }, [formValuesState, form]);

     useEffect(() => {
          let debounceTimer: NodeJS.Timeout;

          const currentTransferRequestNo = formValuesState?.transfer_request_no;
          const currentFormValues = formValuesState;
          const currentData = { form: currentFormValues, table: dataSource };
          const status = formValuesState?.status;

          const currentDataString = JSON.stringify(currentData);
          const lastSavedDataString = JSON.stringify(lastSavedData);

          if (currentTransferRequestNo && (status === "Draft" || status === 1)) {
               if (currentDataString !== lastSavedDataString) {
                    setAutosaveStatus("processing");

                    const saveFunction = async () => {
                         try {
                              const payload = {
                                   ...currentFormValues,
                                   created: userData?.kode_grup1,
                                   status: currentFormValues.status === "Draft" ? 1 : currentFormValues.status,
                                   transfer_request_date: currentFormValues.transfer_request_date
                                        ? moment(currentFormValues.transfer_request_date).format(
                                             "YYYY-MM-DD HH:mm:ss",
                                        )
                                        : null,
                                   approval_date: currentFormValues.approval_date
                                        ? moment(currentFormValues.approval_date).format("YYYY-MM-DD HH:mm:ss")
                                        : null,
                                   items: dataSource.map((item) => ({
                                        item_code: item.item_code,
                                        item_name: item.item_name,
                                        qty: item.qty,
                                        uom: item.uom,
                                   })),
                              };

                              await axios.post(
                                   `${process.env.REACT_APP_BACKEND_URL}/api/wh-transfer-request/autosave`,
                                   payload,
                              );
                              setAutosaveStatus("idle");
                              setLastSavedData(currentData);
                         } catch (error) {
                              console.error("Autosave failed:", error);
                              setAutosaveStatus("error");
                         }
                    };

                    debounceTimer = setTimeout(saveFunction, 1000);
               } else {
                    setAutosaveStatus("idle");
               }
          } else {
               setAutosaveStatus("stopped");
               setLastSavedData(null);
          }

          return () => {
               clearTimeout(debounceTimer);
          };
     }, [formValuesState, dataSource, lastSavedData, userData]);

     const onDateChange: DatePickerProps["onChange"] = (date, dateString) => {
          console.log("Selected Date: ", dateString);
     };

     const onChange = (value: string) => {
          form.setFieldsValue({ warehouse_destination: value });
     };

     const onWarehouseOriginChange = async (value: string) => {
          form.setFieldsValue({ warehouse_origin: value });
          setOriginWarehouse(value);
          if (value && !form.getFieldValue('transfer_request_no')) {
               try {
                    const response = await axios.get(
                         `${process.env.REACT_APP_BACKEND_URL}/api/wh-transfer-request-no`,
                    );
                    const generatedNo = response.data.transferRequestNo;
                    form.setFieldsValue({ transfer_request_no: generatedNo });

                    setFormValuesState(form.getFieldsValue(true));
               } catch (error) {
                    console.error("Error generating WH Transfer Request No:", error);
                    form.setFieldsValue({ transfer_request_no: "Error generating number" });
                    setAutosaveStatus("error");

                    setFormValuesState(form.getFieldsValue(true));
               }
          } else {
               form.setFieldsValue({ transfer_request_no: "" });

               setFormValuesState(form.getFieldsValue(true));
          }
     };

     const columns: TableColumnsType<DataType> = [
          { title: "Item Number", dataIndex: "item_code" },
          { title: "Item Name", dataIndex: "item_name" },
          { title: "Qty", dataIndex: "qty" },
          { title: "UoM", dataIndex: "uom" },
          { title: "Location Rack", dataIndex: "location_code_rack" },
     ];

     const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
     const rowSelection: TableProps<DataType>["rowSelection"] = {
          selectedRowKeys,
          onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
     };

     const getSelectPopupContainer = (triggerNode: HTMLElement) => triggerNode.parentNode as HTMLElement;

     const showAddLineModal = () => {
          const originWarehouseValue = form.getFieldValue("warehouse_origin");
          if (!originWarehouseValue) {
               Modal.warning({
                    title: "Warehouse Origin Required",
                    content: "Please select a warehouse origin before adding an item.",
               });
          } else {
               setIsAddLineModalVisible(true);
          }
     };

     const handleCancelAddLineModal = () => {
          setIsAddLineModalVisible(false);
     };

     const handleAddLine = (selectedItems: ItemDataType[]) => {
          const newItems = selectedItems.filter(
               (newItem) => !dataSource.some((existingItem) => existingItem.key === newItem.key),
          );
          setDataSource([...dataSource, ...newItems]);
          setIsAddLineModalVisible(false);
     };

     const handleDelete = () => {
          const newDataSource = dataSource.filter((item) => !selectedRowKeys.includes(item.key));
          setDataSource(newDataSource);
          setSelectedRowKeys([]);
     };

     const showEditModal = (record: ItemDataType) => {
          setEditingItem(record);
          setIsEditModalVisible(true);
     };

     const handleCancelEditModal = () => {
          setIsEditModalVisible(false);
          setEditingItem(null);
     };

     const handleSaveEditedItem = (key: React.Key, newQty: number) => {
          setDataSource((prevDataSource) =>
               prevDataSource.map((item) => (item.key === key ? { ...item, qty: newQty } : item)),
          );
          handleCancelEditModal();
     };

     const handleAccept = async () => {
          try {
               await form.validateFields(['remark']);
               const currentFormValues = form.getFieldsValue(true);
               const payload = {
                    ...currentFormValues,
                    created: userData?.kode_grup1,
                    status: 2,
                    approval: userData?.kode_grup1,
                    transfer_request_date: currentFormValues.transfer_request_date
                         ? moment(currentFormValues.transfer_request_date).format("YYYY-MM-DD HH:mm:ss")
                         : null,
                    items: dataSource.map((item) => ({
                         item_code: item.item_code,
                         item_name: item.item_name,
                         qty: item.qty,
                         uom: item.uom,
                    })),
               };
               await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/api/wh-transfer-request/autosave`,
                    payload,
               );
               form.setFieldsValue({ status: "Accept", approval_date: moment() });
               setIsFormDisabled(true);
               setIsReceiptMode(false);
               notification.success({ message: "Transfer request has been accepted successfully." });
          } catch (error: any) {
               console.error("Accept failed:", error);
               if (!error.errorFields) {
                    Modal.error({
                         title: "Accept Failed",
                         content: "An error occurred while accepting the request.",
                    });
               }
          }
     };

     const handleReject = async () => {
          try {
               const currentFormValues = form.getFieldsValue(true);
               const payload = {
                    ...currentFormValues,
                    created: userData?.kode_grup1,
                    status: 4,
                    approval: userData?.kode_grup1,
                    transfer_request_date: currentFormValues.transfer_request_date
                         ? moment(currentFormValues.transfer_request_date).format("YYYY-MM-DD HH:mm:ss")
                         : null,
                    approval_date: currentFormValues.approval_date
                         ? moment(currentFormValues.approval_date).format("YYYY-MM-DD HH:mm:ss")
                         : null,
                    items: dataSource.map((item) => ({
                         item_code: item.item_code,
                         item_name: item.item_name,
                         qty: item.qty,
                         uom: item.uom,
                    })),
               };
               await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/api/wh-transfer-request/autosave`,
                    payload,
               );
               form.setFieldsValue({ status: "Reject" });
               setIsFormDisabled(true);
               setIsReceiptMode(false);
               notification.success({ message: "Transfer request has been rejected successfully." });
          } catch (error) {
               console.error("Reject failed:", error);
               Modal.error({
                    title: "Reject Failed",
                    content: "An error occurred while rejecting the request.",
               });
          }
     };

     const handleSubmit = async () => {
          if (autosaveStatus === 'processing') return;
          try {
               const isTableIncomplete = dataSource.some(item => !item.item_code || !item.item_name || item.qty <= 0);
               if (isTableIncomplete || dataSource.length === 0) {
                    Modal.error({ title: "Incomplete Data", content: "Please ensure all items have Code, Name, and Quantity greater than 0." });
                    return;
               }
               const transferRequestNo = form.getFieldValue("transfer_request_no");
               if (!transferRequestNo) {
                    Modal.error({
                         title: "Cannot Submit",
                         content: "Please select an origin warehouse to generate a Transfer Request No first.",
                    });
                    return;
               }
               await form.validateFields();
               const currentFormValues = form.getFieldsValue(true);
               const payload = {
                    ...currentFormValues,
                    created: userData?.kode_grup1,
                    status: 3,
                    transfer_request_date: currentFormValues.transfer_request_date
                         ? moment(currentFormValues.transfer_request_date).format("YYYY-MM-DD HH:mm:ss")
                         : null,
                    approval_date: currentFormValues.approval_date
                         ? moment(currentFormValues.approval_date).format("YYYY-MM-DD HH:mm:ss")
                         : null,
                    items: dataSource.map((item) => ({
                         item_code: item.item_code,
                         item_name: item.item_name,
                         qty: item.qty,
                         uom: item.uom,
                    })),
               };
               await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/api/wh-transfer-request/autosave`,
                    payload,
               );
               form.setFieldsValue({ status: "Ready" });
               setAutosaveStatus("stopped");
               Modal.success({
                    title: "Success",
                    content: "Transfer request has been submitted successfully.",
                    onOk: () => {
                         form.resetFields();
                         setDataSource([]);
                         setOriginWarehouse("");
                         setSelectedRowKeys([]);
                         setLastSavedData(null);

                         form.setFieldsValue({
                              status: "Draft",
                              transfer_request_date: moment(),
                              created: userData?.nama,
                         });
                         setFormValuesState(form.getFieldsValue(true));
                    },
               });
          } catch (error) {
               console.error("Submit failed:", error);
               Modal.error({
                    title: "Submit Failed",
                    content: "An error occurred while submitting the request. Please check the console for details.",
               });
          }
     };

     const handleVoid = async () => {
          if (autosaveStatus === 'processing') return;
          const transferRequestNo = form.getFieldValue("transfer_request_no");
          if (!transferRequestNo) {
               Modal.error({
                    title: "Cannot Void",
                    content: "No Transfer Request Number found to void.",
               });
               return;
          }
          Modal.confirm({
               title: "Are you sure you want to void this transfer request?",
               content: "This action will permanently delete the request from the database.",
               okText: "Yes, Void",
               okType: "danger",
               cancelText: "No",
               onOk: async () => {
                    try {
                         await axios.delete(
                              `${process.env.REACT_APP_BACKEND_URL}/api/wh-transfer-request/void/${transferRequestNo}`
                         );
                         notification.success({ message: "Transfer request voided successfully." });
                         form.resetFields();
                         setDataSource([]);
                         setOriginWarehouse("");
                         setSelectedRowKeys([]);
                         setLastSavedData(null);
                         form.setFieldsValue({
                              status: "Draft",
                              transfer_request_date: moment(),
                              created: userData?.nama,
                         });
                         setFormValuesState(form.getFieldsValue(true));
                    } catch (error) {
                         console.error("Void failed:", error);
                         Modal.error({
                              title: "Void Failed",
                              content: "An error occurred while voiding the request.",
                         });
                    }
               },
          });
     };

     const handleNew = () => {
          if (autosaveStatus === 'processing') return;
          form.resetFields();
          setDataSource([]);
          setOriginWarehouse("");
          setSelectedRowKeys([]);
          setLastSavedData(null);
          setIsReceiptMode(false);
          setIsFormDisabled(false);
          form.setFieldsValue({
               status: "Draft",
               transfer_request_date: moment(),
               created: userData?.nama,
          });
          setFormValuesState(form.getFieldsValue(true));
          notification.info({ message: "Form has been reset." });
     };

     return {
          form,
          onDateChange,
          onChange,
          onWarehouseOriginChange,
          columns,
          rowSelection,
          getSelectPopupContainer,
          warehouses,
          isAddLineModalVisible,
          showAddLineModal,
          handleCancelAddLineModal,
          originWarehouse,
          dataSource,
          handleAddLine,
          handleDelete,
          isEditModalVisible,
          editingItem,
          showEditModal,
          handleCancelEditModal,
          handleSaveEditedItem,
          autosaveStatus,
          handleFormValuesChange,
          handleSubmit,
          handleVoid,
          isFormDisabled,
          isReceiptMode,
          handleAccept,
          handleReject,
          isAcceptRejectDisabled,
          handleNew,
     };
};

export default useItemWHTransferRequestLogic;
